import { useCallback } from 'react';
import useSessionStatus from '@tools/sessionUtils/useSessionStatus';
import { SessionStatus } from '@tools/sessionUtils/SessionStatus';
import { parseISO } from 'date-fns';
import {
  LIVE_STATUS_VENDORS,
  determineVendor,
  Vendor,
  WebcastFormat
} from '@tools/sessionUtils/Vendor';

/**
 * Hook to consolidate isLive logic for Live Status API
 */
// todo:Embedded Cvent Video Conferencing Update this for using meeting status
const useIsLive = ({ webcast, sessionStatus, sessionStart }) => {
  const format = webcast?.format;
  const vendor = determineVendor(webcast, sessionStatus);
  const duration = webcast?.player?.duration;
  const simuliveOffset = webcast?.player?.simuliveOffset;

  const hasDuration =
    format === WebcastFormat.PRE_RECORDED &&
    (vendor === Vendor.CVP || vendor === Vendor.CVENT_VOD) &&
    duration;

  let videoStartDate = new Date(sessionStart);
  let videoEndDate = new Date();
  if (hasDuration) {
    if (simuliveOffset) {
      videoStartDate = new Date(
        parseISO(sessionStart).getTime() -
          simuliveOffset * 1000 /* seconds to millis */
      );
    }
    videoEndDate = new Date(videoStartDate.getTime() + duration);
  }

  // Only used for duration-enabled pre-recorded/simulive sessions
  const sessionStatusByVideoDuration = useSessionStatus({
    start: videoStartDate,
    end: videoEndDate,
    refreshRate: hasDuration ? 1000 : 30000 // For content-based "live", we need a faster refresh interval
  });

  return useCallback(() => {
    if (webcast) {
      // Show live pill based on the Brightcove stream status
      if (format === WebcastFormat.LIVE && LIVE_STATUS_VENDORS.has(vendor)) {
        const streamStatus = webcast?.player?.stream?.status;

        // Do not show live pill if earlier than 5 minutes before session start time
        return sessionStatus === SessionStatus.LESS_THAN_FIVE_MIN_BEFORE ||
          sessionStatus === SessionStatus.DURING ||
          sessionStatus === SessionStatus.LESS_THAN_ONE_HOUR_AFTER ||
          sessionStatus === SessionStatus.MORE_THAN_ONE_HOUR_AFTER ||
          sessionStatus === SessionStatus.AFTER
          ? streamStatus === 'Processing'
          : false;
      }
      if (hasDuration) {
        // Show live pill based on the Brightcove video duration (pre-recorded/simulive)
        return sessionStatusByVideoDuration === SessionStatus.DURING;
      }
    }
    return sessionStatus === SessionStatus.DURING;
  }, [
    webcast,
    sessionStatus,
    sessionStatusByVideoDuration,
    format,
    hasDuration,
    vendor
  ]);
};

export default useIsLive;
